import React, { createContext } from 'react';
import isEqual from 'lodash/isEqual';
import memoizeOne from 'memoize-one';

export type ContentComponentsContextValue = {
	contentComponentsTitle: string | null;
	contentComponentsDisabled: boolean;
	isDraftLockedForEditing?: boolean;
	handleTitleChange: (newTitle: string) => void;
	breadcrumbsClickHandler: (string) => Promise<void>;
	beforeUpdateRequest: () => void;
	postUpdateRequest: () => void;
};

export type ContentComponentsContextProviderProps = {
	value: ContentComponentsContextValue;
	children?: React.ReactNode;
};

export const ContentComponentsContext = createContext<ContentComponentsContextValue | null>(null);

const memoizeDeepEq = memoizeOne(<T,>(obj: T) => obj, isEqual);

export function ContentComponentsContextProvider({
	children,
	value,
}: ContentComponentsContextProviderProps) {
	const memoizedValue = memoizeDeepEq(value);

	return (
		<ContentComponentsContext.Provider value={memoizedValue as ContentComponentsContextValue}>
			{children}
		</ContentComponentsContext.Provider>
	);
}

export function useContentComponentsContext() {
	const context = React.useContext(ContentComponentsContext);
	if (!context) {
		throw new Error(
			'useContentComponentsContext must be used within a ContentComponentsContextProvider',
		);
	}
	return context;
}
